﻿import {
  Category,
  CategorySpecificLiveData,
  Game,
  GameTypes,
  LiveData,
  LobbyTile,
  GameLaunch
} from '../apiTypes/GameService';
import { Favorite } from '../apiTypes/FavoriteService';
import ApiRequest from '@SharedScripts/Helpers/ApiRequest';
import { getOsType } from '../helpers/utils';
import { assertGuid, assertLoggedIn } from '../assertions';
import { Guid, isGuid, BingoScreenNameForGame } from '../Types';
import { SearchResult } from '../apiTypes/GameService/models/SearchResult';

const baseUrl = DS.Config.CASINO_API_URL;
const baseCasinoUrl = `${baseUrl}/casino`;
const baseGameUrl = `${baseCasinoUrl}/game`;
const baseGameV1Url = `${baseGameUrl}/v1`;
const baseLobbyV1Url = `${baseGameUrl}/lobby/v1`;

const baseFavoritesUrl = `${baseUrl}/favorite/v1/Casino/Game`;
const baseRecentUrl = `${baseUrl}/recent/v1/Casino/Game`;

const baseScApiUrl = '/dli/scapi';

const urls = {
  games: `${baseGameV1Url}/games`,
  related: `${baseGameV1Url}/related`,
  winners: `${baseGameV1Url}/winners`,
  externalId: `${baseGameV1Url}/externalIdToInternalId`,
  favorites: baseFavoritesUrl,
  recent: baseRecentUrl,
};

const headers = {
  'x-os': getOsType()
};

export const getRecentlyPlayed = async (count: number = 24) => {
  await assertLoggedIn();
  return await ApiRequest<Game[]>({
    url: `${baseGameV1Url}/game/recentlyPlayed?amountToReturn=${count}`,
    withCredentials: true,
    headers
  });
};

export const getLobbyTiles = async (type: GameTypes) => {
  const url = `${baseLobbyV1Url}?type=${type}`;
  return await ApiRequest<LobbyTile[] | undefined>({ url, headers });
};

export const getCategory = async (categoryId: string) => {
  assertGuid(categoryId);
  const url = `${baseLobbyV1Url}/category/${categoryId}`;
  return await ApiRequest<Category>({ url, headers });
};

export const getCategoryWinners = async (categoryId: string, count: number) => {
  assertGuid(categoryId);
  const url = `${urls.winners}/category/${categoryId}?amount=${count}`;
  return await ApiRequest<Game[] | undefined>({ url, headers });
};

export const getGameLauncher = async (gameServiceId: string, mode: string, seat?: number) => {
  const selectedSeat = seat ? `?selectedSeat=${seat}` : '';
  const url = `${baseGameV1Url}/games/${gameServiceId}/launch/${mode}${selectedSeat}`;

  return await ApiRequest<Promise<GameLaunch>>({
    url,
    headers
  });
};

export const getFavorite = async () => {
  await assertLoggedIn();
  const url = baseFavoritesUrl;
  return await ApiRequest<Favorite>({
    url,
    withCredentials: true,
    headers
  });
};

export const getGame = async (gameId: string) => {
  assertGuid(gameId);
  const url = `${urls.games}/${gameId}`;
  return await ApiRequest<Game | undefined>({ url, headers });
};

export const getScreenNameData = async (bingoGameId: string) => {
  const url = `${baseScApiUrl}/danskespil/gamevendorgvc/launchUrl?gameId=${bingoGameId}`;
  return await ApiRequest<BingoScreenNameForGame>({ url, headers });
};

export const getGames = async (gameIds: string[]) => {
  gameIds = gameIds.filter(isGuid);
  if (gameIds.length === 0) {
    return Promise.reject(new Error('No game ids was provided to "getGames" in CasinoApi'));
  }
  const url = `${urls.games}?gameids=${gameIds.join('&gameids=')}`;

  return await ApiRequest<Game[]>({ url, headers });
};

export const getGameWinners = async (gameId: string, count: number) => {
  assertGuid(gameId);
  const url = `${urls.winners}/game/${gameId}?amount=${count}`;
  return await ApiRequest<Game[] | undefined>({ url, headers });
};

export const getSpecificLiveData = async (categoryId: string) => {
  assertGuid(categoryId);
  const url = `${urls.games}/category/${categoryId}/specificLiveData`;
  return await ApiRequest<CategorySpecificLiveData | undefined>({ url, headers });
};

export const getLiveData = async (gameIds: string[]) => {
  gameIds = gameIds.filter(isGuid);
  if (gameIds.length === 0) {
    return Promise.reject(new Error('No game ids was provided to "getLiveData" in CasinoApi'));
  }
  const url = `${urls.games}/livedata`;

  return await ApiRequest<LiveData[]>({
    url,
    method: 'POST',
    body: gameIds,
    headers: {
      ...headers,
      'content-type': 'application/json'
    }
  });
};

export const getRelated = async (gameId: string) => {
  assertGuid(gameId);
  const url = urls.related + '/' + gameId;

  return await ApiRequest<Game[]>({ url, headers });
};

export const getInternalIdFromExternalId = async (externalId: string) => {
  if (!externalId) return Promise.reject('No external ID provided');

  const url = baseUrl + '/casino/game/v1/externalIdToInternalId/' + externalId;

  return ApiRequest<{id: Guid}>({ url, headers });
};

export const postFavorite = async (gameId: string) => {
  assertGuid(gameId);
  const url = urls.favorites;

  return await ApiRequest<number>({
    url,
    method: 'POST',
    withCredentials: true,
    headers: {
      ...headers,
      'content-type': 'application/json'
    },
    body: {
      entityId: gameId
    }
  });
};

export const deleteFavorite = async (gameId: string) => {
  assertGuid(gameId);
  await assertLoggedIn();
  const url = `${urls.favorites}/${gameId}`;

  return await ApiRequest<number>({
    url,
    method: 'DELETE',
    withCredentials: true,
    headers
  });
};

export const deleteSuperFavorite = async (gameId: string) => {
  assertGuid(gameId);
  await assertLoggedIn();
  const url = `${urls.favorites}/highlight/${gameId}`;

  return await ApiRequest<number>({
    url,
    method: 'DELETE',
    withCredentials: true,
    headers
  });
};

export const postSuperFavorite = async (gameId: string) => {
  assertGuid(gameId);
  await assertLoggedIn();
  const url = `${urls.favorites}/highlight`;

  return await ApiRequest<number>({
    url,
    method: 'POST',
    withCredentials: true,
    headers,
    body: {
      entityId: gameId
    }
  });
};

export const getPopularSearches = async () => {
  return await ApiRequest<Game[]>({ url: `${baseGameV1Url}/game/searchInteraction/popular?recentlyPlayedAmountToReturn=6`, headers });
};

export const getGameSearch = (searchQuery: string) => {
  const url = `${urls.games}?name=${searchQuery}`;

  return ApiRequest<Game[]>({
    url,
    headers
  });
};

export const getGameAndProviderSearch = (searchQuery: string) => {
  const url = `${baseGameV1Url}/search?query=${searchQuery}`;

  return ApiRequest<SearchResult>({
    url,
    headers
  });
};

export const postSearchInteraction = async (gameId: string) => {
  await assertLoggedIn();

  const url = urls.recent + '/searchInteraction';

  return await ApiRequest({
    url,
    method: 'POST',
    withCredentials: true,
    headers,
    body: {
      entityId: gameId
    }
  });
};

export const deleteSearchInteractions = async () => {
  await assertLoggedIn();

  const url = urls.recent + '/searchInteraction';

  return await ApiRequest({
    url,
    withCredentials: true,
    method: 'DELETE',
    headers
  });
};

export const getSearchInteraction = async (count: number = 24) => {
  await assertLoggedIn();
  return await ApiRequest<Game[]>({
    url: `${baseGameV1Url}/game/searchInteraction?amountToReturn=${count}`,
    withCredentials: true,
    headers
  });
};